import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import AnnouncementIcon from '@mui/icons-material/Announcement'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import { AnnouncementSchema } from '../../utils/validation'
import PropTypes from 'prop-types'

const AnnouncementModal = ({ announcements, action, open, onClose, createAnnouncement, announcementId, updateAnnouncement }) => {

  const [initialValues, setInitialValues] = useState({
    title: '',
    endDate: null,
    description: '',
    link: ''
  })
  
  const [show, setShow] = useState(false)

  const announcementForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: AnnouncementSchema,
    onSubmit: (values) => {
      if (announcementId) {
        updateAnnouncement.mutate({
          ...values,
          id: announcementId
        })
      } else {
        createAnnouncement.mutate(values)
      }
    }
  })

  useEffect(() => {
    if (open) {
      setInitialValues({
        title: '',
        endDate: null,
        description: '',
        link: ''
      })

      setShow(false)
    }
  }, [open])

  useEffect(() => {
    if (announcementId) {
      const foundAnnouncement = announcements.find((announcement) => announcement.Id === announcementId)
      setInitialValues({
        title: foundAnnouncement.Title,
        endDate: foundAnnouncement.EndDate,
        description: foundAnnouncement.Description,
        link: foundAnnouncement.Link
      })
    }
  }, [announcementId])

  useEffect(() => {
    if (createAnnouncement.isError || updateAnnouncement.isError) {
      setShow(true)
    }
  }, [createAnnouncement.isSuccess, updateAnnouncement.isError])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='view-edit-annoucement-title'
      aria-describedby='view-edit-user-description'
    >
      <DialogTitle
        id='view-edit-announcement-title'
        sx={{
          display: 'flex',
          color: '#27417A',
          fontWeight: 'bold'
        }}
      >
        <AnnouncementIcon sx={{ fontSize: 36 }} />
        {action} Announcement
      </DialogTitle>
      <form 
        onSubmit={announcementForm.handleSubmit} 
        noValidate
        autoComplete='off'
      >
        <DialogContent>
          <Box
            sx={{
              mt: 1
            }}
          >
            {
              show && (
                <Alert severity='error' sx={{ mb: 2 }}>Unable to save announcement.</Alert>
              )
            }
            <Grid container spacing={2}>
              <Grid item md={7} xs={12}>
                <TextField
                  id='title'
                  name='title'
                  label='Title'
                  value={announcementForm.values.title}
                  onChange={announcementForm.handleChange}
                  onBlur={announcementForm.handleBlur}
                  error={announcementForm.touched.title && Boolean(announcementForm.errors.title)}
                  helperText={announcementForm.touched.title && announcementForm.errors.title}
                  fullWidth
                />
              </Grid>
              <Grid item md={5} xs={12}>
                <DesktopDatePicker
                  label='End Date'
                  value={announcementForm.values.endDate}
                  onChange={(value) => announcementForm.setFieldValue('endDate', value)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth
                      error={announcementForm.touched.endDate && Boolean(announcementForm.errors.endDate)}
                      helperText={announcementForm.touched.endDate && announcementForm.errors.endDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id='description'
                  name='description'
                  label='Description'
                  value={announcementForm.values.description}
                  onChange={announcementForm.handleChange}
                  onBlur={announcementForm.handleBlur}
                  error={announcementForm.touched.description && Boolean(announcementForm.errors.description)}
                  helperText={announcementForm.touched.description && announcementForm.errors.description}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id='link'
                  name='link'
                  label='Link'
                  value={announcementForm.values.link}
                  onChange={announcementForm.handleChange}
                  onBlur={announcementForm.handleBlur}
                  error={announcementForm.touched.link && Boolean(announcementForm.errors.link)}
                  helperText={announcementForm.touched.link && announcementForm.errors.link}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            variant='contained' 
            color='secondary' 
            onClick={onClose}
          >
            Close
          </Button>
          <Button 
            variant='contained' 
            sx={{
              backgroundColor: '#3A6DAA'
            }} 
            type='submit'
            autoFocus
          >
          Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AnnouncementModal.propTypes = {
  announcements: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      endDate: PropTypes.oneOf([PropTypes.instanceOf(Date), null]),
      description: PropTypes.string,
      link: PropTypes.string
    })),
  action: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  createAnnouncement: PropTypes.any,
  updateAnnouncement: PropTypes.any,
  announcementId: PropTypes.number
}

export default AnnouncementModal